import { useEffect, useRef, useState } from 'react';
import { getUser, getUserOrders } from '../api/users.api';
import { mapUser, mapUserOrdersPage, type User, type UserOrdersPage } from '../types/user.types';

export function useUserWorkspace(id: string | undefined) {
  const [user, setUser] = useState<User | undefined>();
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [orderPage, setOrderPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState<UserOrdersPage | undefined>();
  const [ordersLoading, setOrdersLoading] = useState(Boolean(id));
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [userRefreshToken, setUserRefreshToken] = useState(0);
  const mountedRef = useRef(true);
  const ordersRequestRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    if (!id) {
      setUser(undefined);
      setLoading(false);
      return () => { mountedRef.current = false; };
    }
    setLoading(true);
    setError(null);
    getUser(id)
      .then((data) => { if (mountedRef.current) setUser(mapUser(data)); })
      .catch(() => {
        if (mountedRef.current) {
          setUser(undefined);
          setError('خطا در دریافت اطلاعات کاربر');
        }
      })
      .finally(() => { if (mountedRef.current) setLoading(false); });
    return () => { mountedRef.current = false; };
  }, [id, userRefreshToken]);

  useEffect(() => {
    if (!id) {
      setOrdersPage(undefined);
      setOrdersLoading(false);
      return () => undefined;
    }
    const requestId = ++ordersRequestRef.current;
    setOrdersLoading(true);
    setOrdersError(null);
    getUserOrders(id, orderPage, 5)
      .then((data) => {
        if (mountedRef.current && requestId === ordersRequestRef.current) {
          setOrdersPage(mapUserOrdersPage(data));
        }
      })
      .catch(() => {
        if (mountedRef.current && requestId === ordersRequestRef.current) {
          setOrdersPage(undefined);
          setOrdersError('خطا در دریافت سفارش‌های کاربر');
        }
      })
      .finally(() => {
        if (mountedRef.current && requestId === ordersRequestRef.current) {
          setOrdersLoading(false);
        }
      });
    return () => undefined;
  }, [id, orderPage]);

  return {
    user,
    tenant: user?.tenant,
    services: user?.services,
    wallet: user?.wallet,
    orders: user?.orders,
    ordersPage,
    orderPage,
    setOrderPage,
    ordersLoading,
    ordersError,
    refetch: () => setUserRefreshToken((value) => value + 1),
    loading,
    error,
  };
}
